import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { translate, useLanguage } from './i18n/index.js';
import { useApp } from './store.js';
import { DisconnectedState, type Mode } from './states/DisconnectedState.js';
import { browserEntry, showSiteFirst } from './landing/entry.js';
import { LoggedState } from './states/LoggedState.js';
import { PromptHost } from './ui/prompt.js';
import { WebInstallDialog } from './plugins/web/WebInstallDialog.js';
import { Toaster, toast } from './ui/Toaster.js';
import { POPOUT } from './windowMode.js';
import { PopoutApp } from './room/PopoutApp.js';

// o site para quem chega sem conta (docs/spec/landing.md): só quem o vê baixa a página e as imagens
const LandingPage = lazy(() => import('./landing/LandingPage.js').then((module) => ({ default: module.LandingPage })));

export function App() {
  // uma aba da mesa numa janela própria (docs/spec/popout-windows.md)
  if (POPOUT) return <PopoutApp spec={POPOUT} />;
  return <MainApp />;
}

function MainApp() {
  const initialize = useApp((state) => state.initialize);
  const user = useApp((state) => state.user);
  const error = useApp((state) => state.error);
  const dismissError = useApp((state) => state.dismissError);
  const removedNotice = useApp((state) => state.removedNotice);
  const dismissRemovedNotice = useApp((state) => state.dismissRemovedNotice);
  const entryView = useEntryView(!!user);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  // erros do servidor e da API viram avisos rápidos (no login, a própria tela mostra no campo)
  useEffect(() => {
    if (!error || !user) return;
    toast(error, 'error');
    dismissError();
  }, [error, user, dismissError]);

  useEffect(() => {
    if (!removedNotice) return;
    const language = useLanguage.getState().language;
    toast(translate(language, `room.removed.${removedNotice.reason}`, { by: removedNotice.byName }), 'error');
    dismissRemovedNotice();
  }, [removedNotice, dismissRemovedNotice]);

  return (
    <div className="app">
      {user ? (
        <LoggedState />
      ) : entryView.view === 'site' ? (
        <Suspense fallback={<div className="rd-disconnected" />}>
          <LandingPage onSignIn={() => entryView.openForm('login')} onSignUp={() => entryView.openForm('step1')} />
        </Suspense>
      ) : (
        <DisconnectedState key={entryView.view} initialMode={entryView.view} onSite={entryView.siteAllowed ? entryView.openSite : undefined} />
      )}
      {/* antes das perguntas: a confirmação de atualizar um oficial, que sai do catálogo, abre por cima dele */}
      <WebInstallDialog />
      <PromptHost />
      <Toaster />
    </div>
  );
}

/**
 * O que quem não está logado vê (docs/spec/landing.md): o site ou o formulário (entrar ou cadastro).
 * Abrir o formulário pelo site entra no histórico do navegador, sem mudar o endereço, e o Voltar
 * volta ao site. Quem entra e depois sai da conta volta ao formulário de entrar.
 */
function useEntryView(loggedIn: boolean) {
  const [siteAllowed] = useState(() => {
    const entry = browserEntry(null);
    return !entry.desktop && !entry.standalone;
  });
  const [view, setView] = useState<'site' | Mode>(() => (showSiteFirst(browserEntry(useApp.getState().token)) ? 'site' : 'login'));

  // entrou na conta: ao sair, volta ao formulário de entrar
  const [wasLoggedIn, setWasLoggedIn] = useState(loggedIn);
  if (loggedIn !== wasLoggedIn) {
    setWasLoggedIn(loggedIn);
    if (loggedIn) setView('login');
  }

  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      if (useApp.getState().user) return;
      const mode = (event.state as { rdEntry?: Mode } | null)?.rdEntry;
      setView(mode ?? (siteAllowed ? 'site' : 'login'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [siteAllowed]);

  const openForm = useCallback((mode: Mode) => {
    setView(mode);
    window.history.pushState({ rdEntry: mode }, '');
  }, []);
  // o formulário aberto pelo site volta pelo histórico (o Avançar reabre); aberto direto, só troca
  const openSite = useCallback(() => {
    if ((window.history.state as { rdEntry?: Mode } | null)?.rdEntry) window.history.back();
    else setView('site');
  }, []);

  return { view, siteAllowed, openForm, openSite };
}
